import { Badge, Button, buttonClass, cardClass, selectClass, Table } from '@ledgerloom/ui';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi, usePageTitle } from '../hooks';
import { Breadcrumb, Empty, ErrorBanner, Expandable, ListSkeleton, Loading, Reasons, reasonText, schemaLabel, SCHEMA_LABEL, StateChip, Time } from '../components/ui';
import { ImportSummary } from './Approvals';

interface Row { id: string; schema_id: string; state: string; shadow: boolean; origin_agent_id: string | null; origin_user_id: string | null; rationale: string | null; reasons: any[] | null; would: string | null; stale: boolean | null; created_at: string }

// Every proposal and what the gate decided, filtered in the URL; one proposal
// with its decision trail. Patterns: social/activity-feed,
// data-display/filter-panel, and data-display/timeline for the trail.
export function Activity() {
  // Filters live in the URL so back, refresh, and a shared link keep them.
  const [params, setParams] = useSearchParams();
  const state = params.get('state') ?? '';
  const schema = params.get('schema') ?? '';
  const agent = params.get('agent') ?? '';
  // Set by a Home tile: today's finished work of one kind.
  const today = params.get('today') === 'true';
  const done = params.get('done') === 'true';
  const set = (k: string) => (e: { target: { value: string } }) => {
    const p = new URLSearchParams(params);
    if (e.target.value) p.set(k, e.target.value); else p.delete(k);
    setParams(p, { replace: true });
  };
  const filtered = !!(state || schema || agent || today || done);
  usePageTitle('Activity');
  const q = new URLSearchParams({ limit: '100' });
  if (state) q.set('state', state);
  if (schema) q.set('schema', schema);
  if (agent) q.set('agent', agent);
  if (today) q.set('today', 'true');
  if (done) q.set('done', 'true');
  const { data, loading, error } = useApi<Row[]>(`/v1/proposals?${q}`, [state, schema, agent, today, done]);
  const drop = (...keys: string[]) => () => {
    const p = new URLSearchParams(params);
    keys.forEach((k) => p.delete(k));
    setParams(p, { replace: true });
  };
  const agents = useApi<Array<{ id: string }>>('/v1/agents');

  return (
    <div className="stack">
      <div className="page-head"><div><h1>Activity</h1><p>Every proposal from people and agents, and what the gate decided.</p></div></div>
      <fieldset className={`row ${cardClass} card-pad`} style={{ margin: 0 }}>
        <legend className="sr-only">Filter activity</legend>
        <label htmlFor="f-state" className="sr-only">Outcome</label>
        <select id="f-state" className={selectClass} style={{ width: 'auto' }} value={state} onChange={set('state')}>
          <option value="">Any outcome</option><option value="written">Saved</option><option value="applied">Applied</option>
          <option value="pending_approval">Waiting for approval</option><option value="shadowed">Shadow</option><option value="rejected">Rejected</option>
        </select>
        <label htmlFor="f-schema" className="sr-only">Kind</label>
        <select id="f-schema" className={selectClass} style={{ width: 'auto' }} value={schema} onChange={set('schema')}>
          <option value="">Any kind</option>{Object.entries(SCHEMA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label htmlFor="f-agent" className="sr-only">Proposed by</label>
        <select id="f-agent" className={selectClass} style={{ width: 'auto' }} value={agent} onChange={set('agent')}>
          <option value="">People and agents</option>{agents.data?.map((a) => <option key={a.id} value={a.id}>{a.id.split('/')[1]}</option>)}
        </select>
        {(today || done) && (
          <Badge>{[today && 'Today', done && 'finished'].filter(Boolean).join(', ')}
            <Button type="button" variant="ghost" size="sm" onClick={drop('today', 'done')} aria-label="Show any day and any outcome">×</Button>
          </Badge>
        )}
        {filtered && <Button type="button" variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</Button>}
        <span className="faint grow" style={{ textAlign: 'right' }} role="status" aria-live="polite">{data ? `${data.length === 100 ? 'Latest 100' : data.length} proposal${data.length === 1 ? '' : 's'}` : ''}</span>
      </fieldset>
      <ErrorBanner error={error} />
      <div className={cardClass}>
        {loading && !data ? <ListSkeleton label="Loading activity" /> : !data?.length ? (error ? null : <Empty title={filtered ? 'No activity matches' : 'No activity yet'} action={filtered ? <Button type="submit" variant="secondary" size="sm" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</Button> : undefined} />) : (
          <ul className="list" aria-busy={loading} aria-label="Proposals" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {data.map((p) => (
              <li key={p.id}><Link to={`/activity/${p.id}`} className="list-item">
                <div className="grow">
                  <div className="item-title">{schemaLabel(p.schema_id)}</div>
                  <div className="item-sub">{p.origin_agent_id ? `Agent ${p.origin_agent_id.split('/')[1]}` : 'A person'}{p.rationale ? ` · ${p.rationale}` : ''}{p.state === 'rejected' && p.reasons?.[0] ? ` · ${reasonText(p.reasons[0])}` : ''}</div>
                </div>
                {p.origin_agent_id && <Badge tone="info" className="hide-sm">Agent</Badge>}
                {p.would && <Badge>would {p.would.replace('_', ' ')}</Badge>}
                {p.stale && <Badge tone="warning" title="Drafted with a brand profile version that has since been replaced">Stale brand</Badge>}
                <StateChip state={p.state} />
                <span className="faint" style={{ minWidth: 64, textAlign: 'right' }}><Time iso={p.created_at} /></span>
              </Link></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const TONE: Record<string, string> = { written: 'ok', applied: 'ok', approved: 'ok', signoff: 'ok', rejected: 'danger', rejected_by_person: 'danger', expired: 'danger', pending_approval: 'warn', shadowed: 'warn', stale: 'warn' };
const EVENT: Record<string, string> = {
  submitted: 'Submitted', schema_valid: 'Matched its schema', computed: 'Engines computed', rules_passed: 'Hard rules passed',
  pending_approval: 'Routed to a person', approved: 'Approved', written: 'Written by the writer', applied: 'Applied',
  rejected: 'Rejected', shadowed: 'Shadow mode: tested and logged, not written', signoff: 'Signed off', superseded: 'Replaced by an edit',
  expired: 'Expired', withdrawn: 'Withdrawn', stub_writer: 'Reached the stub writer (no ledger in Phase 1)', rejected_by_person: 'Rejected by a person', task_completed: 'Agent closed its task',
  stale: 'Flagged: drafted on a brand profile version that has been replaced',
};

function actorLabel(actor: string) {
  if (actor.startsWith('user:')) return 'a person';
  if (actor.startsWith('agent:')) return `agent ${actor.split('/').pop()}`;
  return ({ 'system:gate': 'the gate', 'system:engines': 'engines', 'system:writer': 'the writer', 'system:config': 'config service' } as Record<string, string>)[actor] ?? actor.replace('system:', '');
}

export function ProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: p, loading, error } = useApi<any>(`/v1/proposals/${id}`, [id]);
  usePageTitle(p ? `${schemaLabel(p.schema_id)} proposal` : null);
  if (loading && !p) return <Loading label="Loading proposal" />;
  if (error || !p) return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/activity', label: 'Activity' }, { label: 'Proposal' }]} />
      <ErrorBanner error={error ?? new Error('This proposal was not found.')} />
      <div><Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/activity">Back to activity</Link></div>
    </div>
  );
  const rules: any[] = p.outcome?.rules ?? [];
  const resultParty = (p.outcome?.result?.party_id ?? p.outcome?.result?.survivor_id) as string | undefined;

  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/activity', label: 'Activity' }, { label: schemaLabel(p.schema_id) }]} />
      <div className="page-head">
        <div>
          <div className="row"><h1>{schemaLabel(p.schema_id)}</h1><StateChip state={p.state} />{p.shadow && <Badge tone="info">Shadow</Badge>}{p.outcome?.stale && <Badge tone="warning">Stale brand</Badge>}</div>
          <p>{p.origin_agent_id ? `Proposed by agent ${p.origin_agent_id}` : 'Proposed by a person'} · {new Date(p.created_at).toLocaleString()} · <span className="mono">{p.schema_id} v{p.schema_version}</span></p>
          {p.run_id && p.origin_agent_id && <p className="small"><Link to={`/agents/${encodeURIComponent(p.origin_agent_id)}/runs/${p.run_id}`}>See the run that proposed this</Link></p>}
        </div>
      </div>
      <div className="grid-main">
        <div className="stack">
          {p.rationale && <div className={`${cardClass} card-pad`}><h2 style={{ fontSize: 14 }}>{p.origin_agent_id ? "Agent's reason" : 'Reason given'}</h2><p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{p.rationale}</p></div>}
          {p.outcome?.reasons?.length > 0 && <div className={`${cardClass} card-pad stack`} style={{ gap: 6 }}><h3>Reasons</h3><Reasons reasons={p.outcome.reasons} /></div>}
          {p.schema_id === 'import.batch' && <div className={`${cardClass} card-pad`}><ImportSummary dry={p.computed?.import_dry_run} source={p.payload.source} policy={p.payload.duplicate_policy} /></div>}
          {p.outcome?.result && (
            <div className={`${cardClass} card-pad stack`} style={{ gap: 6 }}>
              <h2 style={{ fontSize: 14 }}>Result</h2>
              {resultParty && <div><Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={`/contacts/${resultParty}`}>Open the contact</Link></div>}
              {p.schema_id === 'site.change' && p.outcome.result.provider_message_id && <div><a className={buttonClass({ variant: 'secondary', size: 'sm' })} href={p.outcome.result.provider_message_id} target="_blank" rel="noreferrer">Open the pull request</a></div>}
              <Expandable label={['Show the result data', 'Hide the result data']}><pre className="json" tabIndex={0} role="region" aria-label="Result data">{JSON.stringify(p.outcome.result, null, 2)}</pre></Expandable>
            </div>
          )}
          {rules.length > 0 && (
            <div className={cardClass}>
              <div className="card-head"><h2>Rules checked</h2></div>
              <Table label="Rules checked">
                <caption className="sr-only">Rules checked for this proposal</caption>
                <thead><tr><th scope="col">Rule</th><th scope="col">Kind</th><th scope="col">Result</th></tr></thead>
                <tbody>{rules.map((r) => <tr key={r.rule_id}><td><div className="mono">{r.rule_id}</div><div className="faint">{r.reason}</div></td><td>{r.severity === 'hard' ? 'Must pass' : 'Needs a person if hit'}</td><td>{r.violated ? <Badge tone={r.severity === 'hard' ? 'danger' : 'warning'}>Hit</Badge> : <Badge tone="success">Pass</Badge>}</td></tr>)}</tbody>
              </Table>
            </div>
          )}
          <div className={`${cardClass} card-pad stack`} style={{ gap: 8 }}>
            <h2 style={{ fontSize: 14 }}>Payload and engine output</h2>
            <Expandable label={['Show the payload and engine output', 'Hide the payload and engine output']}>
              <div className="stack" style={{ gap: 6 }}><div className="faint">Payload</div><pre className="json" tabIndex={0} role="region" aria-label="Payload">{JSON.stringify(p.payload, null, 2)}</pre><div className="faint">Computed by engines</div><pre className="json" tabIndex={0} role="region" aria-label="Computed by engines">{JSON.stringify(p.computed, null, 2)}</pre></div>
            </Expandable>
          </div>
        </div>
        <div className={`${cardClass} card-pad stack`} style={{ gap: 10 }}>
          <h2>Decision trail</h2>
          <p className="faint" style={{ margin: 0 }}>Hash-chained and append-only. Nothing here can be edited.</p>
          <ol className="timeline">
            {p.trail.map((e: any) => (
              <li key={e.seq} className={TONE[e.event] ?? ''}>
                {EVENT[e.event] ?? e.event}
                <div className="faint">{actorLabel(e.actor)} · <Time iso={e.at} full /></div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
